import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// A deployment does not land on every edge node at the same instant. Reloading
// within the second sometimes asks for a bundle that is not served everywhere
// yet: Pages then answers with index.html and a 200, and public/_headers used
// to freeze that answer for a year under the asset's URL, because the
// /assets/* rule promised `immutable`. The poisoned file was then served to
// everyone, browser disk caches included, until a manual purge of the variant.
// That is what broke the application on 2026-08-21.
//
// So we let the deployment settle. The delay is measured against the AGE of
// the build, not against the moment this client noticed it: a tab opened an
// hour later reloads immediately, and only one landing inside the window waits
// for its next page change.
const SETTLE_AFTER_BUILD_S = 30
const ATTEMPT_KEY = 'version-check-reload'

export default function useVersionCheck() {
    const { pathname } = useLocation()

    useEffect(() => {
        if (import.meta.env.DEV) return

        const check = async () => {
            const response = await fetch('/version.json?_t=' + Date.now())
            const { version } = await response.json()

            if (version === __APP_VERSION__) return

            // The reference time comes from the response's Date header rather
            // than the local clock: a skewed clock would distort the age, and a
            // machine running behind would never reload.
            // In seconds, because that is the granularity of the Date header —
            // it carries nothing finer.
            const age = Date.parse(response.headers.get('date')) / 1000 - version

            if (Number.isFinite(age) && age < SETTLE_AFTER_BUILD_S) return

            // At most one reload per version, otherwise we loop.
            //
            // If version.json and the loaded bundle come from two different
            // deployments — the inconsistency between edge nodes — the
            // comparison stays false AFTER the reload: the page reloads, finds
            // the same gap, and reloads again. This guard cuts the loop at the
            // first attempt; if the gap persists we leave the user on slightly
            // older code rather than in a flickering tab.
            // sessionStorage only stores strings, and `version` is a number, so
            // the comparison has to go through String().
            if (sessionStorage.getItem(ATTEMPT_KEY) === String(version)) return
            sessionStorage.setItem(ATTEMPT_KEY, String(version))

            window.location.reload()
        }

        check().catch(() => {})
    }, [pathname])
}
